// Spacing constants
export const SPACING = {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 24,
};

// Border radius constants
export const RADIUS = {
    sm: 6,
    md: 8,
    lg: 12,
    xl: 16,
};

// Color palette - Billiards themed
export const COLORS = {
    // Primary Colors - Billiard Table Green & Rich Wood
    primaryGreen: '#0D7A3F',
    primaryGreenHover: '#0A5F32',
    primaryGreenLight: '#10954D',
    
    // Secondary Colors - Cue Ball White & Eight Ball Black
    secondaryGold: '#D4A017',
    secondaryGoldHover: '#B8890F',
    secondaryGoldLight: '#E8B94F',
    
    // Accent Colors - Pool Ball Colors
    accentRed: '#DC2626',      // Ball 3, 7
    accentBlue: '#2563EB',      // Ball 2
    accentPurple: '#7C3AED',    // Ball 4
    accentOrange: '#EA580C',    // Ball 5

    // Loyalty tier colors
    loyalty: {
        bronze: '#CD7F32',
        silver: '#A8A9AD',
        gold: '#D4A017',
        platinum: '#7B8CDE',
    },
    
    // Neutral Colors
    white: '#FFFFFF',
    feltGreen: '#1A5F3A',
    woodBrown: '#8B4513',
    gray50: '#F9FAFB',
    gray100: '#F3F4F6',
    gray200: '#E5E7EB',
    gray300: '#D1D5DB',
    gray500: '#6B7280',
    gray600: '#505660',
    gray700: '#374151',
    gray900: '#111827',
    black: '#1A1A1A',
    
    // Status Colors
    success: '#10B981',
    error: '#F29B9B',
    warning: '#F59E0B',
    
    // Table Status Colors
    status: {
        idle: '#10B981',        // Green - Available
        paused: '#FB923C',      // Light Orange - Paused
        active: '#EA580C',       // Orange - Active
        offline: '#1A1A1A',     // Black - Maintenance/Offline
        error: '#DC2626',       // Red - Error
        booked: '#3B82F6',       // Blue - Booked
    },
    // Bill row background by status (light tints)
    billStatus: {
        new: '#FEE2E2',           // light red
        partiallyClosed: '#FEF9C3', // light yellow
        closed: '#D1FAE5',        // light green
    },
    
    // Button Colors
    button: {
        primary: '#FFD700',      // Gold
        primaryBorder: '#FFA500',
        primaryText: '#000000',
        secondary: '#FFFFFF',
        secondaryBorder: '#D1D5DB',
        secondaryText: '#0D7A3F',
    },
    
    // Form Colors
    form: {
        label: '#0D7A3F',
        inputBorder: '#E5E7EB',
        inputBorderError: '#EF4444',
        inputBackground: '#FFFFFF',
        inputText: '#505660',
        placeholder: '#D1D5DB',
        errorBackground: '#FEE2E2',
    },
    
    // Background Colors
    background: {
        primary: '#667EEA',      // Gradient start
        secondary: '#764BA2',    // Gradient end
        card: '#FFFFFF',
    },
    
    // Gradient Colors
    gradients: {
        primary: {
            start: '#667EEA',
            end: '#764BA2',
        },
    },

    // Scoreboard (full-screen tournament display — dark billiard theme)
    scoreboard: {
        background: '#2A2D31',
        panelBg: '#363A3E',
        gold: '#C9B877',
        goldDim: '#8A7D52',
        divider: '#4A4A40',
        clockWarning: '#F59E0B',
        clockCritical: '#EF4444',
        clockCriticalBg: '#3A2226',
    },

    // Reports module (light theme). Semantic tokens reusable by any data table.
    report: {
        borderStrong: '#6B7280',      // control outlines, >= 3:1 on white (WCAG 1.4.11)
        rowAlt: '#F9FAFB',            // zebra rows
        hair: '#E5E7EB',              // hairlines between rows
        headerBg: '#F3F4F6',          // table header, Total row, hover
        hoverBg: '#F3F4F6',
        successText: '#047857',       // positive amounts (>= 4.5:1 on white)
        dangerText: '#B91C1C',        // negative amounts, errors
        focus: '#0D7A3F',             // focus ring
        stickyShadow: 'rgba(17, 24, 39, 0.14)', // right edge of sticky first column once scrolled
    },

    // OBS browser-source overlay (drawn over video; mirrors the stream PNG overlay)
    overlay: {
        text: '#FFFFFF',
        ball: '#EBEBE1',
        clockBg: 'rgba(0, 0, 0, 0.6)',
        clockWarning: '#FBBF24',
        clockCritical: '#F87171',
        clockExpiredBg: 'rgba(185, 28, 28, 0.85)',
        pauseBg: 'rgba(0, 0, 0, 0.65)',
    },
};

// Shadow presets
export const SHADOWS = {
    sm: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.05,
        shadowRadius: 2,
        elevation: 2,
    },
    md: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
    },
    lg: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 8,
        elevation: 5,
    },
    xl: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.2,
        shadowRadius: 16,
        elevation: 8,
    },
};

// Typography
export const TYPOGRAPHY = {
    fontSize: {
        xs: 12,
        sm: 14,
        md: 16,
        lg: 18,
        xl: 20,
        '2xl': 24,
        '3xl': 28,
        '4xl': 32,
        '5xl': 40,
    },
    fontWeight: {
        normal: '400' as const,
        medium: '500' as const,
        semibold: '600' as const,
        bold: '700' as const,
    },
};

// Component sizes (controls, table rows) and responsive breakpoints
export const SIZES = {
    controlDesktop: 40,
    controlTablet: 44,
    controlTouchMax: 48,
    rowDesktop: 36,
    rowTablet: 40,
    rowPhone: 44,
    rowCompact: 32,
    headerDesktop: 40,
    headerTouch: 44,
    railWidth: 240,
    focusRing: 2,
    focusOffset: 2,
};

export const BREAKPOINTS = {
    tablet: 768,
    desktop: 1024,
};

/** Apply to every number, date, picker label and badge. */
export const TABULAR_NUMS = { fontVariant: ['tabular-nums' as const] };

// Theme type for future theme switching support
export type Theme = {
    colors: typeof COLORS;
    spacing: typeof SPACING;
    radius: typeof RADIUS;
    shadows: typeof SHADOWS;
    typography: typeof TYPOGRAPHY;
};

// Default theme (can be extended for dark mode, etc.)
export const defaultTheme: Theme = {
    colors: COLORS,
    spacing: SPACING,
    radius: RADIUS,
    shadows: SHADOWS,
    typography: TYPOGRAPHY,
};
